import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../api/context.js";
import {
  solvesApi,
  type PlacementResponse,
  type WidgetResponse,
} from "../api/playbook-be-client.js";

export function solveWidgetsQueryKey(
  solveId: string,
): readonly ["solve", string, "widgets"] {
  return ["solve", solveId, "widgets"] as const;
}

/**
 * Bulk-hydrates all widget entities pinned to a Solve canvas. Each returned
 * widget is also written into the per-widget query cache so <CanvasWidget>
 * can render without firing one GET /widgets/:id request per widget.
 */
export function useSolveWidgets(solveId: string | undefined) {
  const config = useApiClient();
  const qc = useQueryClient();

  const query = useQuery<PlacementResponse[]>({
    queryKey: solveId
      ? solveWidgetsQueryKey(solveId)
      : ["solve", "", "widgets"],
    queryFn: () => solvesApi.widgets(config, solveId!),
    enabled: Boolean(solveId),
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!query.data) return;
    for (const placement of query.data) {
      // The bulk placement cache can lag a direct widget refetch or optimistic
      // edit. Widget versions are monotonic, so it must never replace an equal
      // or newer per-widget entry when a phase seeds another placement.
      qc.setQueryData<WidgetResponse>(
        ["widget", placement.widget.id],
        (current) =>
          current && current.version >= placement.widget.version
            ? current
            : placement.widget,
      );
    }
  }, [query.data, qc]);

  return query;
}
